import { $createCodeNode } from "@lexical/code";
import {
  CODE,
  type ElementTransformer,
  HEADING as HEADING_ORIG,
  type MultilineElementTransformer,
  ORDERED_LIST as ORDERED_LIST_ORIG,
  QUOTE as QUOTE_ORIG,
  type TextFormatTransformer,
  type TextMatchTransformer,
} from "@lexical/markdown";
import { $isElementNode, $isRootNode, type ElementNode, type LexicalNode } from "lexical";

import { BACKGROUND_ALPHA, DEFAULT_COLOR } from "~/utils/color";

type HighlightCallback = (color: string, isBackground: boolean) => void;

let onHighlightColor: HighlightCallback | null = null;

export const initMarkdown = (callback: HighlightCallback) => {
  onHighlightColor = callback;
};

// Inspired by @lexical/markdown/LexicalMarkdown.dev.js
export const createBlockNode =
  (createNode: (match: string[]) => LexicalNode) =>
  (parentNode: ElementNode, children: LexicalNode[] | null, match: string[]) => {
    const node = createNode(match);
    if ($isElementNode(node) && children) {
      node.append(...children);
    }
    if ($isRootNode(parentNode)) {
      parentNode.append(node);
    } else {
      parentNode.replace(node);
    }

    if ($isElementNode(node)) {
      node.select(0, 0);
    }
  };

// These are the generic lexical transformers, for custom node transformers they should go in the custom node's file
export const CODE_BLOCK: MultilineElementTransformer = {
  ...CODE,
  regExpStart: /^```/,
  replace: createBlockNode(() => $createCodeNode(undefined)),
};

export const HEADING: ElementTransformer = {
  ...HEADING_ORIG,
  regExp: /^(#{1,3})\s/,
};

export const QUOTE: ElementTransformer = {
  ...QUOTE_ORIG,
  regExp: /^>\s/,
};

export const ORDERED_LIST = {
  ...ORDERED_LIST_ORIG,
  regExp: /^(\s*)(\d+)[.)/]{1,2}\s/,
};

export const BOLD_STAR_SINGLE: TextFormatTransformer = {
  format: ["bold"],
  tag: "*",
  type: "text-format",
};

export const ITALICS_HYPHEN: TextFormatTransformer = {
  format: ["italic"],
  tag: "-",
  intraword: false,
  type: "text-format",
};

export const UNDERLINE_UNDERSCORE: TextFormatTransformer = {
  format: ["underline"],
  tag: "_",
  intraword: false,
  type: "text-format",
};

export const STRIKE_THROUGH_TILDE: TextFormatTransformer = {
  format: ["strikethrough"],
  tag: "~",
  type: "text-format",
};

export const CODE_BACKTICK: TextFormatTransformer = {
  format: ["code"],
  tag: "`",
  type: "text-format",
};

export const HIGHLIGHT_EQUALS: TextMatchTransformer = {
  dependencies: [],
  regExp: /=([^=]+)=$/,
  replace: (textNode, match) => {
    textNode.setTextContent(match[1]);
    textNode.setStyle(`background-color: ${DEFAULT_COLOR}${BACKGROUND_ALPHA}`);
    onHighlightColor?.(DEFAULT_COLOR, true);
    return textNode;
  },
  trigger: "=",
  type: "text-match",
};
